/**
 * Proficiency-based technician matching system
 * Calculates deterministic fitness scores for technician-to-job assignments
 */

import { getProficiencyCategory } from '@/constants/proficiencyCategories';
import { getJobDifficultyWeight } from '@/constants/schedulingConstants';
import { cultureNumber } from '@/lib/shopCulture/shopCultureDefaults';
import { UNKNOWN_PROFICIENCY_ORDERING_VALUE, knownProficiencyValue, knownWeightedMean } from '@/lib/assignment/unknownProficiency';

interface Job {
  id: string;
  category: string;
  proficiency_category?: string;
  skill_level: number;
  standard_minutes: number;
  bay_requirement?: string;
}

interface Technician {
  id: string;
  name: string;
  proficiencies: Record<string, number>;
  efficiency_percentage?: number;
  buffer_percentage?: number;
  skill_tier: number;
  bay_assignments?: string[];
}

interface Ticket {
  id: string;
  jobs: Job[];
  is_comeback?: boolean;
  original_tech_id?: string;
  predicted_minutes: number;
}

interface ShopCulture {
  workload_balance_preference: number; // 0-1 (0=strict balance, 1=prefer lighter)
  comeback_nudge: number; // 0-100%
  specialist_gap_splitting: number; // points threshold
  willingness_to_reassign: number; // 0-100%
  diagnosticWeightEnabled?: boolean; // CORE-I/CORE-BI (Core, not Catalyst): shop culture `diagnostic_weight_enabled`; the live consumer is smart-assign's diagnostic rung (_shared/diagnosticCompetencyWeighting.ts)
}

interface WorkloadMap {
  [techId: string]: number; // minutes scheduled
}

export interface FitnessScoreBreakdown {
  totalScore: number;
  tps: number; // Ticket Proficiency Score (weighted by job difficulty)
  efs: number | null; // Earliest Feasible Start (minutes from midnight)
  proficiencyScore: number;
  tierGapPenalty: number;
  avgSkillLevel: number;
  complexityWeight: number;
  efficiencyScore: number;
  workloadScore: number;
  comebackBonus: number;
  specialistPenalty: number;
  idleGapScore: number;
  avgProficiency: number;
  proficiencyGap: number;
  historicalAffinityBonus: number;
  diagnosticQualityBonus: number; // CORE-I (Core): DCS-based bonus (Tweak #3: NEVER negative)
}

/**
 * Calculate TPS (Ticket Proficiency Score) - Weighted proficiency by job difficulty
 * Formula: TPS(ticket, tech) = Σ(w_j × prof_j) / Σ(w_j)
 * Where w_j = job difficulty weight based on skill level
 *
 * Roadmap #8: a category with no stored value is UNKNOWN, not 0. Unknown
 * categories are omitted (value AND weight); with nothing known the result is
 * the hidden ordering prior UNKNOWN_PROFICIENCY_ORDERING_VALUE — for ORDERING
 * only. Use the *Known* variant wherever the number is shown or compared to a
 * floor.
 */
export function calculateTPS(
  jobs: Job[],
  techProficiencies: Record<string, number>
): number {
  if (jobs.length === 0) return 0;
  const known = calculateKnownTPS(jobs, techProficiencies);
  return known === null ? UNKNOWN_PROFICIENCY_ORDERING_VALUE : known;
}

/** TPS over KNOWN categories only; null when OptiBay knows none of them. */
export function calculateKnownTPS(
  jobs: Job[],
  techProficiencies: Record<string, number>
): number | null {
  const entries: Array<{ value: number | null; weight: number }> = [];
  for (const job of jobs) {
    // Get standardized proficiency category name
    const proficiencyCategory = job.proficiency_category
      ? getProficiencyCategory(job.proficiency_category)
      : getProficiencyCategory(job.category);
    const raw = knownProficiencyValue((techProficiencies || {})[proficiencyCategory]);
    entries.push({
      value: raw === null ? null : Math.max(0, Math.min(100, raw)),
      weight: getJobDifficultyWeight(job.skill_level),
    });
  }
  return knownWeightedMean(entries);
}

/**
 * Calculate average proficiency for a set of jobs (legacy - use TPS for ranking)
 * Uses exact category matching with standardized proficiency categories
 *
 * Roadmap #8: a category with no stored value is UNKNOWN, not 0. Unknown
 * categories are omitted (value AND weight); with nothing known the result is
 * the hidden ordering prior UNKNOWN_PROFICIENCY_ORDERING_VALUE — for ORDERING
 * only. Use the *Known* variant wherever the number is shown or compared to a
 * floor.
 */
export function calculateAvgProficiency(
  jobs: Job[],
  techProficiencies: Record<string, number>
): number {
  if (jobs.length === 0) return 0;
  const known = calculateKnownAvgProficiency(jobs, techProficiencies);
  return known === null ? UNKNOWN_PROFICIENCY_ORDERING_VALUE : known;
}

/** Average over KNOWN categories only; null when OptiBay knows none of them. */
export function calculateKnownAvgProficiency(
  jobs: Job[],
  techProficiencies: Record<string, number>
): number | null {
  return knownWeightedMean(jobs.map(job => {
    // RESOLVED, not raw. `job.proficiency_category` may hold either the
    // catalogue key or the canonical name, and only the resolved form is a
    // key of `technicians.proficiencies` — reading the raw value here found
    // nothing and scored 0, so demonstrated capability was invisible to the
    // decision.
    const category = getProficiencyCategory(job.proficiency_category || job.category);
    return { value: knownProficiencyValue((techProficiencies || {})[category]) };
  }));
}

/**
 * Find the maximum KNOWN proficiency among all technicians for a ticket. An
 * unknown technician is not evidence of a maximum.
 */
export function findMaxProficiency(
  ticket: Ticket,
  allTechs: Technician[]
): number {
  const known = allTechs
    .map(tech => calculateKnownAvgProficiency(ticket.jobs, tech.proficiencies))
    .filter((v): v is number => v !== null);
  return known.length ? Math.max(...known) : 0;
}

/**
 * Get complexity weight based on average job skill level (L1-L10)
 * Exponential scaling makes proficiency increasingly critical for higher skill levels
 * L1-2: 0.25 weight (basic tasks, proficiency matters 25%)
 * L3-4: 0.50 weight (intermediate, proficiency matters 50%)
 * L5-6: 1.00 weight (advanced, proficiency critical)
 * L7-8: 1.50 weight (expert, proficiency dominates)
 * L9-10: 2.00 weight (master, proficiency paramount)
 */
function getComplexityWeight(avgSkillLevel: number): number {
  if (avgSkillLevel <= 2) return 0.25;
  if (avgSkillLevel <= 4) return 0.50;
  if (avgSkillLevel <= 6) return 1.00;
  if (avgSkillLevel <= 8) return 1.50;
  return 2.00;
}

/**
 * Calculate tier gap penalty to prevent mismatched assignments
 * Penalizes giving simple jobs to high-tier techs and complex jobs to low-tier techs
 */
function calculateTierGapPenalty(avgSkillLevel: number, techSkillTier: number): number {
  const TIER_GAP_MULTIPLIER = 3.0; // Tunable constant
  
  // Calculate gap: positive = overtier (giving simple job to expert)
  //                negative = undertier (giving complex job to junior)
  const gap = Math.abs(avgSkillLevel - techSkillTier);
  
  return gap * TIER_GAP_MULTIPLIER;
}

/**
 * Calculate comprehensive fitness score for a technician-ticket pairing
 * Lower score = better fit
 */
export function calculateFitnessScore(
  ticket: Ticket,
  tech: Technician,
  shopCulture: ShopCulture,
  currentWorkload: WorkloadMap,
  allTechs: Technician[],
  proposedStartTime?: Date,
  lastTicketEndTime?: Date,
  historicalAffinity?: number
): FitnessScoreBreakdown {
  // Calculate average job skill level
  const avgSkillLevel = ticket.jobs.reduce((sum, j) => sum + j.skill_level, 0) / ticket.jobs.length;

  // NEW: Calculate TPS (Ticket Proficiency Score) - weighted by job difficulty
  const tps = calculateTPS(ticket.jobs, tech.proficiencies);

  // Factor 1: Complexity-Weighted Proficiency (40% weight)
  const avgProficiency = calculateAvgProficiency(ticket.jobs, tech.proficiencies);
  const complexityWeight = getComplexityWeight(avgSkillLevel);
  const weightedProficiency = avgProficiency * complexityWeight;
  const proficiencyScore = 100 - weightedProficiency; // Inverse: 100% prof = 0 points

  // Factor 1b: Tier Gap Penalty (15% weight)
  const tierGapPenalty = calculateTierGapPenalty(avgSkillLevel, tech.skill_tier);

  // Factor 2: Efficiency (10% weight - reduced from 15%)
  const efficiencyPct = tech.efficiency_percentage || 100;
  const efficiencyScore = 100 - efficiencyPct; // Higher efficiency = lower score

  // Factor 3: Workload Balance (20% weight)
  const techWorkload = currentWorkload[tech.id] || 0;
  const totalWorkload = Object.values(currentWorkload).reduce((sum, w) => sum + w, 0);
  const avgWorkload = allTechs.length > 0 ? totalWorkload / allTechs.length : 0;
  const workloadGap = Math.max(0, techWorkload - avgWorkload);
  const balancePref = cultureNumber(shopCulture, 'workload_balance_preference');
  const workloadScore = workloadGap * (1 - balancePref) * 2; // Scale factor

  // Factor 4: Comeback Nudge (10% weight)
  const isComeback = ticket.is_comeback && ticket.original_tech_id === tech.id;
  const comebackStrength = cultureNumber(shopCulture, 'comeback_nudge');
  const comebackBonus = isComeback ? -(comebackStrength / 10) : 0; // Negative = bonus

  // Factor 5: Specialist Gap Penalty (10% weight)
  const specialistGapThreshold = cultureNumber(shopCulture, 'specialist_gap_splitting');
  const maxProficiency = findMaxProficiency(ticket, allTechs);
  // An unknown technician carries no evidence of a specialist gap.
  const knownAvgProficiency = calculateKnownAvgProficiency(ticket.jobs, tech.proficiencies);
  const proficiencyGap = knownAvgProficiency === null ? 0 : maxProficiency - knownAvgProficiency;
  const specialistPenalty = proficiencyGap > specialistGapThreshold 
    ? proficiencyGap * 2  // Double the gap as penalty
    : 0;

  // Factor 6: Idle Gap (5% weight) - Compaction preference
  let idleGapScore = 0;
  if (proposedStartTime && lastTicketEndTime) {
    const gapMinutes = (proposedStartTime.getTime() - lastTicketEndTime.getTime()) / (1000 * 60);
    idleGapScore = Math.max(0, gapMinutes - 15) / 10; // Penalize gaps >15 min
  }

  // Factor 7: Historical Affinity Bonus (20% weight)
  const historicalAffinityBonus = historicalAffinity 
    ? -((historicalAffinity / 100) * 50) // 100% affinity = -50 points (negative = bonus)
    : 0;

  // Combine all factors with weights including historical affinity
  const totalScore = 
    (proficiencyScore * 0.35) +       // Weighted proficiency (35% - reduced from 55%)
    (tierGapPenalty * 0.15) +         // Tier mismatch penalty (15%)
    (efficiencyScore * 0.10) +        // Efficiency (10%)
    (workloadScore * 0.15) +          // Workload balance (15% - increased from 10%)
    historicalAffinityBonus +         // Historical affinity (20% via bonus)
    comebackBonus +                   // Comeback bonus (variable)
    (specialistPenalty * 0.05) +      // Specialist gap (5%)
    (idleGapScore * 0.05);            // Idle gap (5%)

  return {
    totalScore,
    tps, // NEW: Include TPS in breakdown
    efs: null, // NEW: Will be calculated by EFS algorithm
    proficiencyScore,
    tierGapPenalty,
    avgSkillLevel,
    complexityWeight,
    efficiencyScore,
    workloadScore,
    comebackBonus,
    specialistPenalty,
    idleGapScore,
    avgProficiency,
    proficiencyGap,
    historicalAffinityBonus,
    diagnosticQualityBonus: 0, // Phase F: Will be populated when diagnostic Smart Assign is enabled
  };
}

/**
 * PROFICIENCY BAND THRESHOLD
 * Technicians within this percentage of TPS are considered "tied" for proficiency
 * This enables workload balancing among equally-proficient technicians
 * 5% = tight bands (only very similar proficiency counts as tie)
 */
const PROFICIENCY_TIE_THRESHOLD = 5; // 5% TPS difference = tied

/**
 * Rank all technicians for a ticket based on TRUE PROFICIENCY-FIRST ranking
 * 
 * LEXICOGRAPHIC SORT ORDER (proficiency is KING):
 * 1. Proficiency Band (TPS grouped into 5% bands - higher is better)
 * 2. Workload Balance (within same proficiency band - lighter is better)
 * 3. EFS (earlier start is better, null goes last)
 * 4. Total fitness score (final fallback only)
 * 
 * This ensures that a more proficient tech is ALWAYS ranked higher than
 * a less proficient tech, regardless of efficiency or availability.
 * Workload balance only applies as tie-breaker when proficiency is equal.
 */
export function rankTechniciansForTicket(
  ticket: Ticket,
  technicians: Technician[],
  shopCulture: ShopCulture,
  currentWorkload: WorkloadMap
): Array<{ tech: Technician; fitness: FitnessScoreBreakdown }> {
  const rankings = technicians.map(tech => ({
    tech,
    fitness: calculateFitnessScore(ticket, tech, shopCulture, currentWorkload, technicians),
  }));

  // Calculate proficiency band for each tech (bucket TPS into 5% bands)
  // Band 20 = 95-100%, Band 19 = 90-95%, etc.
  const getProficiencyBand = (tps: number): number => Math.floor(tps / PROFICIENCY_TIE_THRESHOLD);

  // LEXICOGRAPHIC SORT: Proficiency-First with Workload Balance Tie-Breaker
  rankings.sort((a, b) => {
    const aBand = getProficiencyBand(a.fitness.tps);
    const bBand = getProficiencyBand(b.fitness.tps);

    // PRIMARY: Proficiency Band (descending - higher band is better)
    // This is the KING - a tech in band 18 always beats band 17
    if (aBand !== bBand) {
      return bBand - aBand;
    }

    // SECONDARY: Workload Balance (ascending - prefer lighter tech)
    // Only kicks in when proficiency bands are EQUAL
    const aWorkload = currentWorkload[a.tech.id] || 0;
    const bWorkload = currentWorkload[b.tech.id] || 0;
    if (aWorkload !== bWorkload) {
      return aWorkload - bWorkload;
    }

    // TERTIARY: EFS (ascending - earlier is better, null goes last)
    const aEfs = a.fitness.efs ?? 9999;
    const bEfs = b.fitness.efs ?? 9999;
    if (aEfs !== bEfs) {
      return aEfs - bEfs;
    }

    // QUATERNARY: Total fitness score (final fallback)
    return a.fitness.totalScore - b.fitness.totalScore;
  });

  return rankings;
}

/**
 * Check if technician meets hard requirements for ticket
 */
export function meetsHardRequirements(
  ticket: Ticket,
  tech: Technician
): { valid: boolean; reason?: string } {
  // Check skill tier
  const maxSkillRequired = Math.max(...ticket.jobs.map(j => j.skill_level));
  if (maxSkillRequired > tech.skill_tier + 1) {
    return {
      valid: false,
      reason: `Tech skill tier ${tech.skill_tier} insufficient for skill level ${maxSkillRequired}`,
    };
  }

  // Check bay requirements
  const requiredBays = [...new Set(ticket.jobs.map(j => j.bay_requirement).filter(Boolean))];
  const techBays = tech.bay_assignments || [];
  
  const missingBays = requiredBays.filter(bay => !techBays.includes(bay));
  if (missingBays.length > 0) {
    return {
      valid: false,
      reason: `Tech lacks required bay types: ${missingBays.join(', ')}`,
    };
  }

  return { valid: true };
}

/**
 * Filter technicians by bay requirements before ranking
 */
export function filterTechniciansByBayRequirements(
  ticket: Ticket,
  technicians: Technician[]
): Technician[] {
  return technicians.filter(tech => meetsHardRequirements(ticket, tech).valid);
}
